import { useMemo } from "react";
import { useQuery } from "react-query";
import { z } from "zod";
import { ForwardType, InstrumentSchema, OptionType, SettlementPeriod } from "~/types";
import { agent } from "~/util/agent";

const OptionComboInstrument = z.object({
  name: z.string(),
  order_side: z.string(),
  ratio: z.number().optional(),
});

const OptionsComboIndicationSchema = z.object({
  instruments: z.array(OptionComboInstrument),
  size: z.string(),
});

const OptionsComboIndicationResponseSchema = z.object({
  instruments: z.array(OptionComboInstrument),
  quote_currency_name: z.string(),
  ask: z.string(),
  bid: z.string(),
  size: z.string(),
  delta: z.string(),
  timestamp: z.string(),
});

const OptionsSchema = InstrumentSchema.extend({
  id: z.string().uuid().min(1),
  contract_size_unit: z.string(),
  name: z.string(),
  strike_price: z.string(),
  option_type: z.nativeEnum(OptionType),
  venue_name: z.string(),
  open_interest: z.string(),
});

const OptionsIndicationSchema = z.object({
  ask: z.string().optional(),
  ask_details: z.string().optional(),
  bid: z.string().optional(),
  bid_details: z.string().optional(),
  venue_name: z.string(),
});

const OptionsInfoSchema = z.object({
  id: z.string().uuid().min(1),
  name: z.string().min(1),
  closest: z.boolean().optional(),
  enabled: z.boolean(),
  venue_name: z.string().min(1),
  mark_price: z.string().min(1),
  underlying_price: z.string().min(1),
  underlying_currency: z.string().min(1),
  open_interest: z.string().min(1),
  quote_currency: z.string(),
  size_currency: z.string(),
  expiration_timestamp: z.string(),
  mark_iv: z.string().min(1),
  bid_iv: z.string().min(1),
  ask_iv: z.string().min(1),
  delta: z.string().min(1),
  theta: z.string().min(1),
  gamma: z.string().min(1),
  vega: z.string().min(1),
  rho: z.string().min(1),
  strike_price: z.string(),
  option_type: z.nativeEnum(OptionType),
  min_trade_amount: z.string(),
  contract_size: z.string(),
  tick_size: z.string(),
  bid_price: z.string(),
  ask_price: z.string(),
  bid_size: z.string(),
  ask_size: z.string(),
  bid_depth: z.string(),
  ask_depth: z.string(),
});

const ForwardsInfoSchema = z.object({
  id: z.string().uuid().min(1),
  name: z.string().min(1),
  timestamp: z.string().min(1),
  venue_name: z.string().min(1),
  mark_price: z.string().min(1),
  open_interest: z.string().min(1),
  underlying_price: z.string().min(1),
  quote_currency: z.string(),
  size_currency: z.string(),
  expiration_timestamp: z.string(),
  bid_price: z.string(),
  ask_price: z.string(),
  bid_size: z.string(),
  ask_size: z.string(),
  bid_depth: z.string(),
  ask_depth: z.string(),
});

type OptionsComboIndicationResponseType = z.infer<typeof OptionsComboIndicationResponseSchema>;
type OptionsComboIndicationType = z.infer<typeof OptionsComboIndicationSchema>;
type OptionIndicationResourcetype = z.infer<typeof OptionsIndicationSchema>;
type OptionResourceType = z.infer<typeof OptionsSchema>;
type OptionInfoResourceType = z.infer<typeof OptionsInfoSchema>;
type ForwardInfoResourceType = z.infer<typeof ForwardsInfoSchema>;

// Classify a forward structurally rather than by name. The /instruments/forwards/info
// payload has expiration_timestamp: null for perps (no settlement_period), while the raw
// /instruments/forwards payload carries settlement_period. Prefer settlement_period when
// present and fall back to a missing expiry so both shapes are covered.
const isPerpetualForward = (item: { expiration_timestamp?: string | null; settlement_period?: string }) =>
  item.settlement_period != null
    ? item.settlement_period === SettlementPeriod.PERPETUAL
    : !item.expiration_timestamp;

const QUERY_OPTIONS_KEY = "options";
const QUERY_FORWARDS_KEY = "forwards";

const useOptionIndication = (
  id?: string,
  config?: {
    size?: string;
    cost?: string;
    currency?: string;
  },
) => {
  const params = new URLSearchParams();

  if (config?.size) params.append("indicator_size", config.size);
  if (config?.cost) params.append("indicator_cost", config.cost);
  if (config?.currency) params.append("indicator_currency", config.currency);

  return useQuery(
    [QUERY_OPTIONS_KEY, id, "indication", config?.size, config?.cost, config?.currency],
    async () => agent(`instruments/options/${id}/indication?${params.toString()}`).json<OptionIndicationResourcetype>(),
    {
      enabled: !!id,
      refetchInterval: 30000,
    },
  );
};

const useOptionComboIndication = (data: OptionsComboIndicationType) => {
  const ratios = useMemo(() => data.instruments.map((d) => d.ratio).join("_"), [data.instruments]);
  const names = useMemo(() => data.instruments.map((d) => d.name).join("_"), [data.instruments]);
  const sides = useMemo(() => data.instruments.map((d) => d.order_side).join("_"), [data.instruments]);

  return useQuery(
    [QUERY_OPTIONS_KEY, "combo", "indication", data.size, ratios, names, sides],
    async () =>
      agent.post(`instruments/options/combo/indication`, { json: data }).json<OptionsComboIndicationResponseType>(),
    {
      refetchInterval: 30000,
      enabled: data.instruments.length > 0,
    },
  );
};

const useOptionInfo = () => {
  return useQuery([QUERY_OPTIONS_KEY, "info"], async () =>
    agent(`instruments/options/info`).json<OptionInfoResourceType[]>(),
  );
};

const useForwardInfo = () => {
  return useQuery([QUERY_FORWARDS_KEY, "info"], async () =>
    agent(`instruments/forwards/info`).json<ForwardInfoResourceType[]>(),
  );
};

const useInstrumentInfo = (forwards?: boolean, forwardType?: string) => {
  const query = useQuery(
    [!forwards ? QUERY_OPTIONS_KEY : QUERY_FORWARDS_KEY, "info"],
    async () => agent(`instruments/${!forwards ? "options" : "forwards"}/info`).json<OptionInfoResourceType[]>(),
    {
      refetchOnMount: "always",
    },
  );

  const filteredData = useMemo(() => {
    if (!forwards || !forwardType || !query.data) {
      return query.data;
    }

    const filtered = query.data.filter((item) => {
      const isPerpetual = isPerpetualForward(item);
      return forwardType === ForwardType.PERPETUAL ? isPerpetual : !isPerpetual;
    });

    return filtered;
  }, [forwards, forwardType, query.data]);

  return {
    ...query,
    data: filteredData as OptionInfoResourceType[] | undefined,
  };
};

const useOption = (id: string) => {
  return useQuery([QUERY_OPTIONS_KEY, id], async () => agent(`instruments/options/${id}`).json<OptionResourceType>());
};

const useOptions = () => {
  return useQuery([QUERY_OPTIONS_KEY], async () => agent("instruments/options").json<OptionResourceType[]>());
};

const useInstruments = (forwards?: boolean, forwardType?: string) => {
  const query = useQuery([!forwards ? QUERY_OPTIONS_KEY : QUERY_FORWARDS_KEY], async () =>
    agent(`instruments/${forwards ? "forwards" : "options"}`).json<OptionResourceType[]>(),
  );

  const filteredData = useMemo(() => {
    if (!forwards || !forwardType || !query.data) {
      return query.data;
    }
    return query.data.filter((item) => {
      const isPerpetual = isPerpetualForward(item);
      return forwardType === ForwardType.PERPETUAL ? isPerpetual : !isPerpetual;
    });
  }, [forwards, forwardType, query.data]);

  return {
    ...query,
    data: filteredData,
  };
};

export {
  isPerpetualForward,
  useForwardInfo,
  useInstrumentInfo,
  useInstruments,
  useOption,
  useOptionComboIndication,
  useOptionIndication,
  useOptionInfo,
  useOptions,
};
export type {
  OptionIndicationResourcetype,
  OptionInfoResourceType,
  OptionResourceType,
  OptionsComboIndicationResponseType,
  OptionsComboIndicationType,
};
